/**
 * A hub's strip of tabs (the Navigation review, slice (c)): pages that belong together, each still at its own address,
 * joined under one strip across the top of the page. The strip is a row of links, so a tab opens, links and goes back
 * like any page; on a phone it scrolls sideways, the open tab brought into view. A hub whose viewer sees one tab only
 * draws no strip. The strip lines up with the open page's title (its PageContainer's width and padding).
 *
 * HubLayout is the router's layout route over a hub's pages: the strip, then the page, and it keeps the tab a person
 * opens as the one the hub opens at next time (hub-memory).
 */
import { useEffect, useRef } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { cn } from "@/lib/ui/cn";
import { useAuth } from "@/lib/auth";
import { hubPending, hubTabAt, hubTabs, type Hub, type HubTab, type PageWidth } from "@/lib/nav/hubs";
import { rememberHubTab } from "@/lib/nav/hub-memory";
import { useAppMenu } from "@/lib/nav/use-app-menu";

const WIDTH: Record<PageWidth, string> = {
  xl: "max-w-xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
  "6xl": "max-w-6xl",
  "7xl": "max-w-7xl",
};

/** A tab's look, and the strip's height while it waits for the org's packs. */
const TAB = "shrink-0 whitespace-nowrap px-3 py-2.5 text-sm font-medium transition-colors";

export function HubTabs({
  hub,
  tabs,
  active,
  pending = false,
}: {
  hub: Hub;
  tabs: readonly HubTab[];
  active?: string;
  /** Which tabs show is not known yet (hubPending), so the strip keeps its room, empty (#1217 and #1224 reviews). */
  pending?: boolean;
}) {
  const open = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    // A phone's strip scrolls sideways: the open tab is never left off its edge (#1217 review, LOW 3).
    open.current?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }, [active, tabs.length]);
  const width = WIDTH[hub.tabs.find((t) => t.id === active)?.width ?? "6xl"];
  const row = cn("mx-auto flex w-full items-stretch gap-1 overflow-x-auto px-4 md:px-8", width);

  if (pending) {
    return (
      <div aria-hidden data-hub-pending={hub.id} className="border-b border-border bg-surface">
        <div className={row}>
          <span className={cn(TAB, "invisible")}>{hub.label}</span>
        </div>
      </div>
    );
  }
  if (tabs.length < 2) return null;
  return (
    <nav aria-label={hub.label} data-hub={hub.id} className="border-b border-border bg-surface">
      <div className={row}>
        <span className="mr-2 flex shrink-0 items-center text-[10px] font-semibold uppercase tracking-wider text-on-surface-muted">
          {hub.label}
        </span>
        {tabs.map((t) => (
          <Link
            key={t.id}
            ref={t.id === active ? open : undefined}
            to={t.to}
            data-hub-tab={t.id}
            aria-current={t.id === active ? "page" : undefined}
            // The open tab reads in the page's own text, underlined in it: the graphite brand on its surface reads at
            // about 1.4:1 in the dark theme, as text (the menu's #1211 fix) and as a line alike. The line is an inset
            // shadow in the text's own colour, so it keeps the text's contrast in either theme. Forced colours drop
            // shadows and colour, so there the open tab is underlined text (LOW 4).
            className={cn(
              TAB,
              t.id === active
                ? "text-on-surface shadow-[inset_0_-2px_0_0_currentColor] forced-colors:underline forced-colors:underline-offset-4"
                : "text-on-surface-muted hover:text-on-surface",
            )}
          >
            {t.label}
          </Link>
        ))}
      </div>
    </nav>
  );
}

/** The layout route over a hub's pages: its strip of tabs, then the page, which it keeps as the hub's next tab. */
export function HubLayout({ hub }: { hub: Hub }) {
  const { pathname } = useLocation();
  const { context, landing, reads } = useAppMenu();
  const person = useAuth().user?.id ?? "";
  const tab = hubTabAt(hub, pathname)?.id;
  useEffect(() => {
    if (tab) rememberHubTab(person, hub.id, tab);
  }, [person, hub.id, tab]);
  return (
    <>
      <HubTabs hub={hub} tabs={hubTabs(hub, context)} active={tab} pending={hubPending(hub, landing.packsKnown, reads)} />
      <Outlet />
    </>
  );
}
